import { useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import HaloRing from "@/components/HaloRing";
import { ImportWizard } from "@/components/ImportWizard";

export default function DataPage() {
  const [busy, setBusy] = useState({});

  const setB = (k, v) => setBusy((s) => ({ ...s, [k]: v }));

  const downloadCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch { toast.error("Export failed"); }
  };

  const seedDemo = async () => {
    setB("seed", true);
    try {
      const { data } = await api.post("/seed/demo");
      const c = data.created;
      toast.success(`Demo seeded — ${c.people} people, ${c.companies} companies, ${c.invoices} invoice, +more`);
    } catch { toast.error("Seed failed"); }
    finally { setB("seed", false); }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL of your data? This cannot be undone.")) return;
    setB("clear", true);
    try {
      const { data } = await api.post("/seed/clear");
      const total = Object.values(data.deleted).reduce((s, n) => s + n, 0);
      toast.success(`Deleted ${total} records`);
    } catch { toast.error("Failed"); }
    finally { setB("clear", false); }
  };

  return (
    <div className="space-y-6" data-testid="data-page">
      <div className="flex items-center gap-4">
        <HaloRing size={40}/>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Import &amp; Export</h1>
          <p className="text-sm text-muted-foreground mt-1">Bring contacts in from any export — CSV, Excel, vCard, JSON, TXT or PDF — and take your data back out whenever you like. Load a demo dataset to explore, or wipe everything and start fresh.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="studio-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Download className="w-4 h-4"/> Export</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Download all of your people and companies as CSV. Keeps advanced fields like credits, unions, agent info, rates.</p>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="export-people-btn" variant="outline" onClick={() => downloadCsv("/export/people.csv", "halo-people.csv")}><Download className="w-4 h-4 mr-2"/> People CSV</Button>
              <Button data-testid="export-companies-btn" variant="outline" onClick={() => downloadCsv("/export/companies.csv", "halo-companies.csv")}><Download className="w-4 h-4 mr-2"/> Companies CSV</Button>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 lg:order-3">
          <ImportWizard />
        </div>

        <Card className="studio-card lg:col-span-2">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4"/> Demo dataset</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Populate your workspace with a realistic sample: one simple person, one advanced person with credits and agent info, one company with services, one active project, one pinned task, one meeting, one sent invoice and one retainer template.</p>
            <div className="flex flex-wrap gap-2 items-center">
              <Button data-testid="seed-demo-btn" onClick={seedDemo} disabled={busy.seed}><Sparkles className="w-4 h-4 mr-2"/>{busy.seed ? "Seeding…" : "Load demo data"}</Button>
              <Button data-testid="clear-all-btn" variant="outline" onClick={clearAll} disabled={busy.clear} className="text-[hsl(var(--studio-danger))] border-[hsl(var(--studio-danger)/0.4)]"><Trash2 className="w-4 h-4 mr-2"/>{busy.clear ? "Clearing…" : "Delete all my data"}</Button>
              <Badge variant="secondary" className="text-[10px] ml-auto">Safe: your account and settings are preserved.</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
